import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const VARIANT_STYLES = {
  court:
    'bg-warning-light text-warning-dark border-warning',
  upcoming:
    'bg-accent/20 text-text-secondary border-border',
};

/**
 * Compact relaxed-matchmaking indicator for court / upcoming headers.
 * Icon-only footprint reduces header reflow when the badge appears alongside a new lineup.
 */
function MatchmakingRelaxedBadge({ relaxed, relaxationDetails, variant = 'court' }) {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const triggerRef = useRef(null);

  const details = Array.isArray(relaxationDetails) ? relaxationDetails : [];
  const hasDetails = details.length > 0;
  const styleClass = VARIANT_STYLES[variant] ?? VARIANT_STYLES.court;

  useEffect(() => {
    if (!isHovered || !hasDetails || !triggerRef.current) return undefined;

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 250;
    const tooltipHeight = 150;

    let top = rect.top - tooltipHeight - 8;
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;

    const viewportWidth = window.innerWidth;

    if (left + tooltipWidth > viewportWidth) {
      left = viewportWidth - tooltipWidth - 8;
    }
    if (left < 8) {
      left = 8;
    }
    if (top < 8) {
      top = rect.bottom + 8;
    }

    setTooltipStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 99999,
    });

    return undefined;
  }, [isHovered, hasDetails]);

  if (!relaxed) return null;

  const badgeClassName = `inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs leading-none ${styleClass} ${
    hasDetails ? 'cursor-help' : ''
  }`;

  const badge = hasDetails ? (
    <span
      ref={triggerRef}
      className={badgeClassName}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="img"
      aria-label="Relaxed matchmaking constraints"
      title="Relaxed matchmaking"
    >
      ⚡
    </span>
  ) : (
    <span
      className={badgeClassName}
      role="img"
      aria-label="Relaxed matchmaking constraints"
      title="Relaxed matchmaking"
    >
      ⚡
    </span>
  );

  if (!hasDetails) return badge;

  return (
    <>
      {badge}
      {isHovered &&
        createPortal(
          <div
            className={`px-3 py-2 text-xs rounded-lg shadow-lg max-w-xs pointer-events-none border ${
              variant === 'upcoming'
                ? 'bg-surface-elevated text-text-primary border-border-medium'
                : 'bg-gray-800 text-white border-transparent'
            }`}
            style={tooltipStyle}
          >
            <div className="font-semibold mb-1.5 text-sm">Relaxed Constraints:</div>
            <ul className="list-disc list-inside space-y-1 text-left">
              {details.map((detail, idx) => (
                <li key={idx} className="leading-relaxed">
                  {typeof detail === 'string' ? detail : detail?.message ?? String(detail)}
                </li>
              ))}
            </ul>
            <div
              className={`absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent ${
                variant === 'upcoming' ? 'border-t-surface-elevated' : 'border-t-gray-800'
              }`}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

export default React.memo(MatchmakingRelaxedBadge);
